class CustomSwiperSlider extends HTMLElement {
	/** @type {import('swiper').Swiper | null} */
	swiper = null;

	/** @type {HTMLElement | null} */
	swiperElement = null;

	/** @type {Object} */
	sliderOptions = {};

	/** @type {string} */
	sliderId = "";

	/** @type {string} */
	sliderBreakpoint = "";

	/** @type {MediaQueryList | null} */
	mediaQuery = null;

	/** @type {boolean} */
	initialized = false;

	connectedCallback() {
		if (this.initialized) {
			return;
		}

		this.initialized = true;
		this.setProperties();
		this.bindEvents();
	}

	disconnectedCallback() {
		if (!this.initialized) {
			return;
		}

		this.destroySlider();

		if (this.mediaQuery) {
			this.mediaQuery.removeEventListener("change", this.handleMediaQueryChange);
			this.mediaQuery = null;
		}

		this.initialized = false;
	}

	setProperties() {
		this.swiperElement = this.querySelector(".swiper");
		this.sliderOptions = JSON.parse(this.dataset.sliderOptions || "{}");
		this.sliderId = this.swiperElement.id;
		this.sliderBreakpoint = this.dataset.sliderBreakpoint;
	}

	bindEvents() {
		this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
		this.boundAddSlideAttributes = this.addSlideAttributes.bind(this);
		this.boundUpdateSlidesAttribute = this.updateSlidesAttribute.bind(this);
		this.boundRemoveSlideAttributes = this.removeSlideAttributes.bind(this);

		if (this.sliderBreakpoint) {
			this.mediaQuery = window.matchMedia(this.sliderBreakpoint);
			this.mediaQuery.addEventListener("change", this.handleMediaQueryChange);

			if (this.mediaQuery.matches) {
				this.initSlider();
			}
		} else {
			this.initSlider();
		}
	}

	/**
	 * @param {MediaQueryListEvent} event
	 */
	handleMediaQueryChange(event) {
		if (event.matches) {
			this.initSlider();
		} else {
			this.destroySlider();
		}
	}

	initSlider() {
		if (this.swiper) {
			return;
		}

		this.swiper = new Swiper(this.swiperElement, {
			...this.getBaseSliderOptions(),
			...this.sliderOptions,
		});

		this.swiper.on("init", this.boundAddSlideAttributes);
		this.swiper.on("slideChange", this.boundUpdateSlidesAttribute);
		this.swiper.on("destroy", this.boundRemoveSlideAttributes);

		this.swiper.init();
	}

	destroySlider() {
		if (!this.swiper) {
			return;
		}

		this.swiper.off("init", this.boundAddSlideAttributes);
		this.swiper.off("slideChange", this.boundUpdateSlidesAttribute);
		this.swiper.destroy(true, true);
		this.swiper = null;
	}

	getBaseSliderOptions() {
		const prevEl = `#swiper-button-prev-${this.sliderId}`;
		const nextEl = `#swiper-button-next-${this.sliderId}`;
		const hasNavButtons = this.querySelector(prevEl) && this.querySelector(nextEl);

		const baseSliderOptions = {
			init: false,
			threshold: 2,
			mousewheel: {
				enabled: true,
				forceToAxis: true,
				releaseOnEdges: true,
			},
			pagination: {
				el: `#swiper-pagination-${this.sliderId}`,
				type: "bullets",
				clickable: true,
			},
			navigation: {
				prevEl,
				nextEl,
			},
		};

		if (hasNavButtons) {
			baseSliderOptions.a11y = {
				prevSlideMessage: theme.strings.previousSlideshow,
				nextSlideMessage: theme.strings.nextSlideshow,
				scrollOnFocus: false,
			};
		}

		return baseSliderOptions;
	}

	/**
	 * @param {object} swiper
	 */
	addSlideAttributes(swiper) {
		swiper.slides.forEach((slide, index) => {
			slide.setAttribute("role", "group");
			slide.setAttribute("aria-roledescription", "slide");
			slide.setAttribute("tabindex", swiper.activeIndex === index ? "0" : "-1");
			slide.setAttribute(
				"aria-current",
				swiper.activeIndex === index ? "true" : "false",
			);
		});
	}

	/**
	 * @param {object} swiper
	 */
	removeSlideAttributes(swiper) {
		swiper.slides.forEach((slide) => {
			slide.removeAttribute("role");
			slide.removeAttribute("aria-roledescription");
			slide.removeAttribute("tabindex");
			slide.removeAttribute("aria-current");
			slide.removeAttribute("aria-label");
		});
	}

	/**
	 * @param {object} swiper
	 */
	updateSlidesAttribute(swiper) {
		swiper.slides.forEach((slide, index) => {
			slide.setAttribute("tabindex", swiper.activeIndex === index ? "0" : "-1");
			slide.setAttribute(
				"aria-current",
				swiper.activeIndex === index ? "true" : "false",
			);
		});
	}
}

if (!customElements.get("custom-swiper-slider")) {
	customElements.define("custom-swiper-slider", CustomSwiperSlider);
}
